'use client';

import Script from 'next/script';

/**
 * Affirm SDK loader.
 *
 * Ports the legacy `loadAffirm.js` bootstrap onto the repo's `next/script` seam
 * (same pattern as `CembraScript`): it sets `window._affirm_config` with the
 * market public key + script URL, then loads the Affirm client library. Once the
 * SDK loads it scans the page for `.affirm-as-low-as` nodes (rendered by
 * `AffirmMessage`) and populates them with the "as low as $X/month" sentence.
 *
 * Market wiring mirrors the legacy per-country switch
 * (`NEXT_PUBLIC_AFFIRM_KEY_*` / `NEXT_PUBLIC_AFFIRM_URL_*`, US default + CA).
 *
 * CONFIG GAP: these `NEXT_PUBLIC_AFFIRM_*` keys are not yet present in the
 * multibrand env profiles. When absent, `hasAffirmConfig` is false so neither
 * the SDK nor the widget node renders — the code path is wired and the only
 * thing missing is the environment configuration.
 */

interface AffirmMarketConfig {
  key: string | undefined;
  url: string | undefined;
}

function getAffirmMarketConfig(countryCode?: string): AffirmMarketConfig {
  if (countryCode?.toLowerCase() === 'ca') {
    return {
      key: process.env.NEXT_PUBLIC_AFFIRM_KEY_CA,
      url: process.env.NEXT_PUBLIC_AFFIRM_URL_CA,
    };
  }
  // Default: US market (the BEC-24058 target market).
  return {
    key: process.env.NEXT_PUBLIC_AFFIRM_KEY_US,
    url: process.env.NEXT_PUBLIC_AFFIRM_URL_US,
  };
}

/** Whether the Affirm public key + script URL are configured for this market. */
export function hasAffirmConfig(countryCode?: string): boolean {
  const { key, url } = getAffirmMarketConfig(countryCode);
  return Boolean(key && url);
}

export interface AffirmScriptProps {
  /** ISO country code used to pick the market key/url (defaults to US). */
  countryCode?: string;
}

export function AffirmScript({ countryCode }: Readonly<AffirmScriptProps>) {
  const { key, url } = getAffirmMarketConfig(countryCode);

  // Without a public key + script URL the SDK cannot initialise; render nothing
  // rather than inject a broken script tag. See CONFIG GAP note above.
  if (!key || !url) return null;

  // Escape `<` so a hostile config value can't break out of the inline <script>
  // (JSON.stringify does not escape `</script>`).
  const configJson = JSON.stringify({ public_api_key: key, script: url }).replace(/</g, '\\u003c');

  return (
    <>
      <Script
        id="affirm-config"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: `window._affirm_config = ${configJson};` }}
      />
      <Script data-testid="affirm-sdk" id="affirm-sdk" src={url} strategy="afterInteractive" />
    </>
  );
}

AffirmScript.displayName = 'AffirmScript';
